import Actions from 'flow/actions';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import moment from 'moment';
import { fetchQueueCallRecordsUnhandled } from 'flow/actions/PbxActions';
import { selectMissedQueueCallKeysByDate } from 'flow/selectors/central-switch-selectors';
import useAppContext from 'flow/hooks/useAppContext';
import { CallRecordItemSkeleton } from 'flow/components/loaders/CallRecordSkeleton';
import CentralSwitchHeader from 'flow/components/central-switch/CentralSwitchHeader';
import CentralSwitchEmptyState from 'flow/components/central-switch/CentralSwitchEmptyState';
import Information from './Information';
import MissedQueueCallsList from './MissedQueueCallsList';

const MissedCallsFromQueuesCentralSwitchWidget = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const callRecordKeysByDate = useAppSelector(
        selectMissedQueueCallKeysByDate,
    );
    const hasCalls = callRecordKeysByDate.size > 0;

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        dispatch(setComponentIsMounted(true));
        return () => {
            dispatch(setComponentIsMounted(false));
        };
    }, [dispatch]);

    useEffect(() => {
        setIsLoading(true);
        const cutoffTime = moment().subtract(72, 'hours').valueOf();
        dispatch(
            fetchQueueCallRecordsUnhandled(
                true,
                false,
                undefined,
                cutoffTime,
                true,
            ),
        ).finally(() => setIsLoading(false));
    }, [dispatch]);

    const renderContent = () => {
        if (isLoading && !hasCalls) {
            return (
                <div className="px-4 pb-4">
                    <CallRecordItemSkeleton />
                </div>
            );
        }
        if (!hasCalls) {
            return <CentralSwitchEmptyState uniqueId="MissedCallsFromQueues" />;
        }
        // The card has a maximum height, so the calls scroll inside it, under
        // their sticky date titles.
        return (
            <div className="min-h-0 overflow-y-auto px-4 pb-4">
                <MissedQueueCallsList
                    callRecordKeysByDate={callRecordKeysByDate}
                />
            </div>
        );
    };

    return (
        <div className="flex min-h-0 flex-col">
            <CentralSwitchHeader>
                <span>
                    {i18next.t(
                        'central_switch.missed_calls_from_queues.description',
                    )}
                </span>
                <Information />
            </CentralSwitchHeader>
            {renderContent()}
        </div>
    );
};

export default MissedCallsFromQueuesCentralSwitchWidget;

const setComponentIsMounted = (isMounted: boolean) => ({
    type: Actions.SET_MISSED_CALLS_WIDGET_IS_MOUNTED,
    isMounted,
});
